import { defineStore } from 'pinia';
import { apiCall } from '@/utils/globalFunctions';
import store from '@/store/store';
import { debounce } from 'lodash';
import { useSubscriptionStore } from '@/store/subscription';

export const useClientsStore = defineStore('clients', {
  state: () => {
    const userId = store.state.user?.user_id || null;
    const storageKey = userId ? `clientsStore_${userId}` : 'clientsStore';
    const persisted = getPersistedState(storageKey);

    return {
      userId,
      clients: persisted.clients || [],
      currentPage: persisted.currentPage || 1,
      itemsPerPage: persisted.itemsPerPage || 10,
      totalPages: persisted.totalPages || 1,
      totalRows: persisted.totalRows || 0,
      searchQuery: '',
      loading: false,
      error: null,
      currentCompanyId: 'all',
      useLocalFiltering: true
    };
  },

  getters: {
    // Combined filter for company and search
    filteredClients: (state) => {
      // If using local filtering or no company selected, filter locally
      if (state.useLocalFiltering || state.currentCompanyId === 'all') {
        let filtered = state.clients;
        
        if (state.currentCompanyId && state.currentCompanyId !== 'all') {
          filtered = filtered.filter(client => 
            client.company_id === state.currentCompanyId
          );
        }
        
        if (state.searchQuery) {
          const query = state.searchQuery.toLowerCase();
          filtered = filtered.filter(client =>
            client.client_name?.toLowerCase().includes(query) ||
            client.client_email?.toLowerCase().includes(query)
          );
        }
        
        return filtered;
      }
      // Otherwise return all clients (will be filtered by API)
      return state.clients;
    },
    paginatedClients: (state) => {
      const start = (state.currentPage - 1) * state.itemsPerPage;
      const end = start + state.itemsPerPage;
      return state.filteredClients.slice(start, end);
    },
    
    totalFilteredRows: (state) => {
      return state.filteredClients.length; // Always use filtered length
    },
    
    totalFilteredPages: (state) => {
      return Math.ceil(state.filteredClients.length / state.itemsPerPage);
    },
    getClientById: (state) => (id) =>
      state.clients.find((client) => client.client_id === id),
    activeClients: (state) =>
      state.clients.filter((client) => client.is_active !== false),
  },

  actions: {
    persistState() {
      // Only persist these specific properties
      setPersistedState(`clientsStore_${this.userId}`, {
        clients: this.clients,
        totalRows: this.totalRows,
        currentPage: this.currentPage,
        itemsPerPage: this.itemsPerPage,
        // Explicitly NOT persisting searchQuery or currentCompanyId
      });
    },
    setUserId(userId) {
      this.userId = userId;
      this.reset();
      if (userId) {
        this.fetchClients();
      }
    },

    reset() {
      this.$reset();
      if (this.userId) {
        localStorage.removeItem(`clientsStore_${this.userId}`);
      }
    },

    resetPagination() {
      this.currentPage = 1;
      this.totalPages = Math.ceil(this.filteredClients.length / this.itemsPerPage);
    },

    async fetchClients() {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      this.loading = true;
      this.resetError();

      try {
        // Only include company filter in API call if not "all"
        const params = {};
        if (this.currentCompanyId !== 'all') {
          params.companyId = this.currentCompanyId;
        }
        if (this.searchQuery && this.currentCompanyId !== 'all') {
          params.search = this.searchQuery;
        }

        const response = await apiCall({
          method: 'GET',
          url: '/Client/getClients',
          params, // Pass filters as query params
          dataType: 'json',
        }, store);

        if (Array.isArray(response.data)) {
          this.clients = response.data;
          this.totalRows = response.data.length;
          this.resetPagination();
          this.persistState();
          
          // Determine if we should use local filtering
          this.useLocalFiltering = this.currentCompanyId === 'all' || !this.searchQuery;
          
          return response.data;
        }
        throw new Error(response.message || 'Invalid client data');
      } catch (err) {
        this.setError(err, 'Failed to fetch clients', 'FETCH_CLIENTS_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    fetchClientsDebounced: debounce(function () {
      return this.fetchClients();
    }, 300),

    async getClientsByCompany(companyId = 'all') {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return [];
      }

      // Handle "all" case - return all clients from local data
      if (companyId === 'all') {
        return this.clients;
      }

      // First check if we have clients for this company in local data
      const cachedClients = this.clients.filter(client => 
        client.company_id === companyId
      );

      // If we have clients locally and they're recent enough, return them
      if (cachedClients.length > 0) {
        return cachedClients;
      }

      return [];
      
    },

    // Add local pagination actions
    setCurrentPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.persistState();
      }
    },
    
    setItemsPerPage(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1; // Reset to first page when changing page size
      this.totalPages = Math.ceil(this.clients.length / itemsPerPage);
      this.persistState();
    },

    async refreshClients() {
      this.loading = true;
      this.resetFilters();
      try {
        await this.fetchClients();
        this.currentPage = 1;
      } finally {
        this.loading = false;
      }
    },

    async addClient(clientData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }

      // Check if company already has max clients
      /*const companyClients = this.clients.filter(
        client => client.company_id === clientData.company_id
      );*/
      
      /*if (companyClients.length >= this.maxClientsPerCompany) {
        this.setError(
          null,
          `Maximum limit of ${this.maxClientsPerCompany} clients per company reached`,
          'CLIENT_LIMIT_REACHED'
        );
        throw new Error(
          `You can only add up to ${this.maxClientsPerCompany} clients per company`
        );
      }*/

      return this.handleClientAction(
        '/Client/addClient',
        clientData,
        'Failed to add client',
        'ADD_CLIENT_FAILED'
      );
    },

    async updateClient(clientData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      return this.handleClientAction(
        '/Client/updateClient',
        clientData,
        'Failed to update client',
        'UPDATE_CLIENT_FAILED'
      );
    },

    async toggleClientStatus(clientId, isActive) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      return this.handleClientAction(
        '/Client/toggleClientStatus',
        { clientId, isActive },
        'Failed to toggle client status',
        'TOGGLE_STATUS_FAILED'
      );
    },

    async deleteClient(clientId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      return this.handleClientAction(
        '/Client/deleteClient',
        { clientId },
        'Failed to delete client',
        'DELETE_CLIENT_FAILED'
      );
    },

    resetFilters() {
      this.searchQuery = '';
      this.currentCompanyId = 'all';
      this.currentPage = 1;
    },

    setCurrentCompany(companyId) {
      this.currentCompanyId = companyId;
      this.currentPage = 1;
      
      // When selecting a specific company, we'll use API filtering
      if (companyId !== 'all') {
        this.useLocalFiltering = false;
        this.fetchClients();
      } else {
        // When selecting "all", switch to local filtering
        this.useLocalFiltering = true;
      }
    },
    
    setSearchQuery: debounce(function(query) {
      this.searchQuery = query;
      this.currentPage = 1;
      
      // Only trigger API search if a specific company is selected
      if (this.currentCompanyId !== 'all') {
        this.useLocalFiltering = false;
        this.fetchClients();
      } else {
        // For "all" companies, use local search
        this.useLocalFiltering = true;
      }
    }, 300),

    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
      // Update total pages based on all clients
      this.totalPages = Math.ceil(this.clients.length / this.itemsPerPage);
    },

    resetError() {
      this.error = null;
    },

    async handleClientAction(url, data, errorCode, defaultMessage) {
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall(
          {
            method: 'POST',
            url,
            data,
            dataType: 'json',
          },
          store
        );

        if (response.status === 'success') {
          await this.fetchClients();
          useSubscriptionStore().fetchUsage();
          return response;
        }

        throw new Error(response.message || defaultMessage);
      } catch (err) {
        this.setError(err, defaultMessage, errorCode);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    setError(err, fallbackMessage, fallbackCode) {
      this.error = {
        message: err?.message || fallbackMessage,
        code: err?.code || fallbackCode,
        timestamp: new Date().toISOString(),
      };
    },
  },
});

// --- Helper functions ---
function getPersistedState(storageKey) {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to parse stored clients state', error);
    return {};
  }
}

function setPersistedState(storageKey, state) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to persist clients state', error);
  }
}
